import {
  aggregateEodBars,
  bucketMinutes,
  dailyFromMinutes,
  fromQfBars,
  INDICATOR_SESSIONS,
  intervalMinutes,
  lookbackSessionsFor,
  recentSessionDates,
  sourceFor,
  type CandleBar,
  type ChartInterval,
  type QfBarRow,
  type SessionFilter,
} from '../components/candle-chart/barSource';
import { todayEt } from '../utils/marketTime';
import { apiClient } from './api';
import { qfRestGet } from './qfClient';

interface BarsResponse {
  bars?: QfBarRow[];
}

interface CmeMinute {
  ts: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface CmeEnvelope {
  data?: { bars?: CmeMinute[] };
}

export interface ChartBarsRequest {
  symbol: string;
  interval: ChartInterval;
  sessionFilter?: SessionFilter;
}

async function getSpotBars(
  symbol: string,
  interval: ChartInterval,
  sessionFilter: SessionFilter,
): Promise<CandleBar[]> {
  const lookback = String(lookbackSessionsFor(interval));
  if (interval.unit === 'week' || interval.unit === 'month') {
    const body = await qfRestGet<BarsResponse>('/v1/bars', {
      symbol,
      interval: '1',
      lookback_sessions: lookback,
      interval_unit: 'day',
    });
    return aggregateEodBars(fromQfBars(body.bars ?? []), interval.unit);
  }
  if (interval.unit === 'day') {
    const body = await qfRestGet<BarsResponse>('/v1/bars', {
      symbol,
      interval: '1',
      lookback_sessions: lookback,
      interval_unit: 'day',
    });
    return fromQfBars(body.bars ?? []);
  }
  const body = await qfRestGet<BarsResponse>('/v1/bars', {
    symbol,
    interval: String(intervalMinutes(interval)),
    lookback_sessions: lookback,
    session_filter: sessionFilter,
    interval_unit: 'minute',
  });
  return fromQfBars(body.bars ?? []);
}

function cmeToBars(rows: readonly CmeMinute[]): CandleBar[] {
  const out: CandleBar[] = [];
  for (const row of rows) {
    const time = Date.parse(row.ts);
    if (!Number.isFinite(time)) continue;
    out.push({
      time,
      open: row.open,
      high: row.high,
      low: row.low,
      close: row.close,
      volume: row.volume ?? 0,
    });
  }
  return out;
}

async function fetchCmeDay(root: string, date: string): Promise<CandleBar[]> {
  const response = await apiClient.get<CmeEnvelope>('/api/market-data/cme/ohlc/', {
    symbol: `${root}.CME`,
    date,
  });
  const bars = response.data?.bars;
  return Array.isArray(bars) ? cmeToBars(bars) : [];
}

async function mapPool<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const index = next;
      next += 1;
      if (index >= items.length) return;
      out[index] = await fn(items[index]);
    }
  };
  const workers = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return out;
}

async function getCmeBars(
  root: string,
  interval: ChartInterval,
  sessionFilter: SessionFilter,
  sessions: number,
): Promise<CandleBar[]> {
  const dates = recentSessionDates(sessions, todayEt());
  const days = await mapPool(dates, 6, (date) => fetchCmeDay(root, date));
  const minutes = days.flat();
  if (interval.unit === 'week' || interval.unit === 'month') {
    return aggregateEodBars(dailyFromMinutes(minutes), interval.unit);
  }
  if (interval.unit === 'day') return dailyFromMinutes(minutes);
  return bucketMinutes(minutes, intervalMinutes(interval), sessionFilter);
}

export async function getBars(request: ChartBarsRequest): Promise<CandleBar[]> {
  const symbol = request.symbol.trim().toUpperCase();
  const sessionFilter = request.sessionFilter ?? 'rth';
  if (sourceFor(symbol) === 'cme') {
    return getCmeBars(symbol, request.interval, sessionFilter, lookbackSessionsFor(request.interval));
  }
  return getSpotBars(symbol, request.interval, sessionFilter);
}

export async function getIndicatorMinutes(symbol: string): Promise<CandleBar[]> {
  const root = symbol.trim().toUpperCase();
  if (sourceFor(root) === 'cme') {
    return getCmeBars(root, { unit: 'minute', count: 1 }, 'rth', INDICATOR_SESSIONS);
  }
  const body = await qfRestGet<BarsResponse>('/v1/bars', {
    symbol: root,
    interval: '1',
    lookback_sessions: String(INDICATOR_SESSIONS),
    session_filter: 'rth',
    interval_unit: 'minute',
  });
  return fromQfBars(body.bars ?? []);
}

export const chartBarsService = {
  getBars,
  getIndicatorMinutes,
};
